// Copyright: IQGeo Limited 2010-2019
import _ from 'underscore';
import Promise from 'bluebird';
import myw from 'myWorld/base/core';
import './semaphore';
import './transaction';

//this function generates 'redirect' methods
//these methods all receive a feature or feature type as the first argument and direct
//the call to the corresponding datasource
const getProxyMethod = methodName =>
    function(featureOrType, ...args) {
        const featureType = typeof featureOrType == 'string' ? featureOrType : featureOrType.type;

        if (!featureType)
            throw new Error(
                `First argument to ${methodName}() should be a string with the feature type`
            );

        const { dsName, typeInDs } = this._decomposeFeatureType(featureType);
        const datasource = this.getDatasource(dsName);
        if (typeof featureOrType == 'string') featureOrType = typeInDs;
        return datasource
            .ensureLoggedIn()
            .then(() => datasource[methodName](...[featureOrType, ...args]));
    };

myw.Database = myw.MywClass.extend('Database', {
    includes: myw.Events,

    /**
     * @class Provides access to the several datasources configured in the system
     * @param  {System} system
     * @param  {string} applicationName
     * @constructs
     * @extends MywClass
     * @fires datasourceState-changed
     * @fires internetStatus-changed
     */
    initialize(system, applicationName) {
        this.applicationName = applicationName;
        this.system = system;

        this._datasourceDefs = {};
        this._datasources = {};

        //to keep interest of application components in feature details. Keyed on aspect name
        this._aspectsInterest = {};

        /** Mode used by nativeApp. Either 'local' or 'master'.
         *   Only used in NativeApp enviroment
         * @type {string} */
        this.nativeAppMode = 'local';

        this._semaphore = new myw.Semaphore();
        myw.trace('database', 1, 'initializing');

        /**
         * Promise that becomes fulfilled when the initialization has completed
         * @type {promise}
         */
        this.initialized = this._ensureDatasourceDefs().then(() => {
            myw.trace('database', 1, 'initialized');
            return this;
        });
    },

    /**
     * Obtains feature DD information for the given feature type
     * @param  {array<univFeatureType>} types
     * @return {promise<object<univFeatureType,featureDD>>} Promise for feature DDs keyed on (universal) feature type
     */
    getDDInfoFor(types) {
        if (!types || types.length === 0) return {};

        const featuresDD = {};
        const typesPerDs = _.groupBy(types, type => {
            const ds = this.getDatasourceForType(type);
            return ds && ds.name;
        });
        delete typesPerDs[undefined];
        const typeInDsFunc = type => this._decomposeFeatureType(type).typeInDs;

        const promisePerDs = _.mapObject(typesPerDs, (types, dsName) => {
            const datasource = this.getDatasource(dsName);
            //convert univFeatureType into "featureTypeInDatasource"
            types = types.map(typeInDsFunc);
            return datasource
                .ensureLoggedIn()
                .call('getDDInfoFor', types)
                .then(results => {
                    //attach  datasource reference to each feature dd
                    //ENH: move this to datasource._ensureDDInfoFor
                    _.each(results, (result, featureType) => {
                        result.datasource = datasource;
                        result.ufn = dsName == 'myworld' ? featureType : `${dsName}/${featureType}`;
                        result['external_name'] = this.system.localise(
                            result.external_name,
                            featureType
                        );
                        featuresDD[result.ufn] = result;
                    });
                });
        });

        return Promise.props(promisePerDs).return(featuresDD);
    },

    /**
     * Returns a new transaction object
     * @return {Transaction}
     */
    transaction() {
        return new myw.Transaction(this);
    },

    /**
     * Run (insert, delete, update) operations on multiple features on one datasource within one transaction
     * @param {Transaction} transaction     Operations to be executed
     * @return {Promise<Array<integer|string>>}  Lists of feature ids
     */
    runTransaction(transaction) {
        return transaction.getOperations().then(operations => {
            const operationsPerDs = _.groupBy(operations, operation => {
                const ds = this.getDatasourceForType(operation[1]);
                return ds && ds.name;
            });
            delete operationsPerDs[undefined];
            const names = Object.keys(operationsPerDs);
            if (names.length > 1)
                throw new Error(`Can only run transaction on one datasource (${names.join(',')})`);
            const ds = this.getDatasource(names[0]);
            if (!ds.ensureLoggedIn || !ds.runTransaction)
                throw new Error(`Datasource ${names[0]} does not support transactions`);

            return ds.ensureLoggedIn().call('runTransaction', transaction);
        });
    },

    /**
     * Signal the start of a user transaction - a set of database operations that shouldn't be intertwined with other processes
     * If successful this will ensure no other user transactions run simultaneously
     * If another user transaction has already been started this call will return a rejected promise
     * @param  {string}  msg                A name/description of the user process to start
     * @param  {boolean} [toWait=false]     If true and there is a current transaction running, the returned promise will not be
     *                                      rejected and will instead be resolved when there is no longer a current user transaction
     * @return {Promise} Resolves if/when the there is no current user transaction. See parameter 'toWait' for behaviour if there is another transaction already running
     */
    beginUserTransaction(msg, toWait = false) {
        if (!this._semaphore.isLocked()) {
            //free to start transaction
            this.currentTransactionName = msg;
            return this._semaphore.lock();
        }

        //there is another transaction running

        if (toWait) {
            return this._semaphore.lock().then(() => {
                this.currentTransactionName = msg;
            });
        } else {
            const message = `${this.msg('not_able_to_start')} ${msg} (${
                this.currentTransactionName
            } ${this.msg('running')})`;
            return Promise.reject(new Error(message));
        }
    },

    /**
     * Signal the end of the current user transaction
     *  @see  beginUserTransaction
     */
    endUserTransaction() {
        this._semaphore.unlock();
    },

    /**
     * Obtains information about accessible features types
     * @return {object<univFeatureType,object>} Keyed on universal feature type
     */
    getFeatureTypes() {
        if (!this._featureTypes) {
            this._featureTypes = {};

            _.each(this._datasourceDefs, (dsDef, dsName) => {
                _.each(dsDef.featureTypes, (featureDD, key) => {
                    const qualFeatureName = `${dsName}/${key}`;
                    this._featureTypes[qualFeatureName] = featureDD;
                });
            });
        }
        return this._featureTypes;
    },

    /**
     * Obtains information about editable feature types
     * @return {object<univFeatureType,object>} Keyed on universal feature type
     */
    getEditableFeatureTypes() {
        const features = this.getFeatureTypes();
        const appEditableFeatureTypes = {};
        Object.keys(features).filter(featureDD => {
            if (features[featureDD].editable) {
                //Only need to check if editable on datasource if editable on featureDD
                const featureInfo = this._decomposeFeatureType(featureDD);
                const ds = this.getDatasource(featureInfo.dsName);
                //Include features editable on datasource
                if (ds.appEditableFeatureTypes[featureInfo.typeInDs])
                    appEditableFeatureTypes[featureDD] = features[featureDD];
            }
        });
        return appEditableFeatureTypes;
    },

    /**
     * Finds the features selectable by a user map click
     * @param  {L.LatLng}   selectionPoint      Point the user clicked/selected
     * @param  {integer}    zoomLevel           Zoom level at time of selection
     * @param  {integer}    pixelTolerance  Number of pixels to use as tolerance for the selection
     * @param  {array<Layer>}   layers      Layers relevant for selection (active and visible)
     * @param  {string}     [worldId]           Defaults to geographical world
     * @return {Promise<Array<Feature>>}  Promise for the features
     */
    select(selectionPoint, zoomLevel, pixelTolerance, layers, worldId) {
        const requests = [],
            //per datasource name
            layersPerDs = {},
            errorHandler = reason => {
                //make sure an error doesn't "stop" the selection flow
                console.log('External selection failure. Reason:', reason);
                return [];
            };

        //get datasources to send selection requests
        _.each(layers, layer => {
            const ds = layer.datasource;
            if (typeof ds.select !== 'function') return; //datasource doesn't support selection, continue

            if (!layersPerDs[ds.name]) {
                layersPerDs[ds.name] = [];
            }
            layersPerDs[ds.name].push(layer);
        });

        //send requests
        _.each(layersPerDs, (layers, dsName) => {
            const request = this.getDatasource(dsName)
                .ensureLoggedIn()
                .then(ds => ds.select(selectionPoint, zoomLevel, pixelTolerance, layers, worldId))
                .catch(errorHandler);

            //put the myWorld request in the first position so that myw results are listed first
            if (dsName == 'myworld') requests.unshift(request);
            else requests.push(request);
        });

        return Promise.all(requests.map(promise => promise.reflect())).then(results => {
            const features = _.chain(results)
                    .map(r => {
                        if (r.isFulfilled()) return r.value();
                    })
                    .compact()
                    .flatten()
                    .value(),
                geomTypeMatches = (types, feature) => {
                    let type;
                    try {
                        type = feature.getGeometryType(worldId);
                    } catch (error) {
                        console.log(error);
                    }
                    return _.contains(types, type);
                };

            //if we have point features, return only point features
            //ENH: Do this on the datasource
            const points = _.filter(features, geomTypeMatches.bind(null, ['Point', 'MultiPoint']));
            if (points.length) return points;
            //if we have linestring features, return only those
            const lines = _.filter(
                features,
                geomTypeMatches.bind(null, ['LineString', 'MultiLineString'])
            );
            if (lines.length) return lines;
            else return features; // this will be empty or polygons
        });
    },

    /**
     * Finds the features selectable by a user inside the given geom
     * @param {string} geom geometry coordinates as string
     * @param {int} zoomLevel  Zoom level at time of selection
     * @param {array<Layer>} layers Layers relevant for selection (active and visible)
     * @returns selectable features intersecting geom
     */
    selectBox(geom, zoomLevel, layers, worldId) {
        // for each datasource get features
        const requests = [];
        const layersPerDs = {},
            errorHandler = reason => {
                //make sure an error doesn't "stop" the selection flow
                console.log('External selection failure. Reason:', reason);
                return [];
            };

        //get datasources to send selection requests
        _.each(layers, layer => {
            const ds = layer.datasource;
            if (typeof ds.selectBox !== 'function') return; //datasource doesn't support selection, continue

            if (!layersPerDs[ds.name]) {
                layersPerDs[ds.name] = [];
            }
            layersPerDs[ds.name].push(layer);
        });

        //send requests
        _.each(layersPerDs, (layers, dsName) => {
            const request = this.getDatasource(dsName)
                .ensureLoggedIn()
                .then(ds => ds.selectBox(geom, zoomLevel, layers, worldId))
                .catch(errorHandler);

            //put the myWorld request in the first position so that myw results are listed first
            if (dsName == 'myworld') requests.unshift(request);
            else requests.push(request);
        });

        // Merge results
        return Promise.all(requests.map(promise => promise.reflect())).then(results => {
            const features = _.chain(results)
                .map(r => {
                    if (r.isFulfilled()) return r.value();
                })
                .compact()
                .flatten()
                .value();

            return features;
        });
    },

    /**
     * Obtains a feature
     * @param  {string}     featureType
     * @param  {string|integer}     featureId   Key that identifies feature in table
     * @return {promise<Feature>}
     * @method
     */
    getFeature: getProxyMethod('getFeature'),

    /**
     * Obtains a feature identified by its urn
     * @param  {urn}   urn      Urn of the desired feature
     * @return {Promise<Feature>}  Promise for the specified feature
     */
    getFeatureByUrn(urn) {
        const parts = urn.split('/'),
            featureType = parts.slice(0, parts.length - 1).join('/'),
            datasource = this.getDatasourceForType(featureType);

        return datasource.ensureLoggedIn().call('getFeatureByUrn', urn);
    },

    /**
     * Get features of a given table optionally constrained by bounding box
     * @param  {univFeatureType}    featureType
     * @param  {queryParameters}    [options]      Filters to apply on results. Check the corresponding datasource for documentation on non-supported options
     * @return {Promise<Array<Feature>>}    Promise to resolve with a list of the matched features
     * @method
     */
    getFeatures: getProxyMethod('getFeatures'),

    /**
     * Queries the database for features filtered on a field criteria
     * @param  {univFeatureType}   featureType  Name of the table/feature type
     * @param  {string}   field     Name of the field to filter on
     * @param  {string}   operator  Operator to use on the filter: "=" or "like"
     * @param  {string}   value     Value to filter on
     * @return {Promise<Array<Feature>>}  Promise for the resulting features
     * @method
     */
    getFeaturesByValue: getProxyMethod('getFeaturesByValue'),

    /**
     * Obtains features of a given type that are close to a point and within a tolerance
     * @param  {string[]}   featureTypes     Types of features to obtain
     * @param  {L.LatLng}   position
     * @param  {Integer}    tolerance       Tolerance in meters
     * @return {Promise<Feature[]>}    Promise for a list with the features which are close to the given point within the specified tolerance
     * @method
     */
    getFeaturesAround(featureTypes, position, tolerance) {
        const decomposed = featureTypes.map(featureType => {
            return this._decomposeFeatureType(featureType);
        });
        const byDs = _(decomposed).groupBy('dsName');

        return Promise.map(_.pairs(byDs), pair => {
            const dsName = pair[0];
            const featureTypes = pair[1];
            const typesInDs = _(featureTypes).pluck('typeInDs');
            const datasource = this.getDatasourceForType(dsName);
            return datasource.getFeaturesAround(typesInDs, position, tolerance);
        }).then(_.flatten);
    },

    /**
     * Sends a query request on a given feature type
     * @param  {queryDefinition}  queryDef
     * @param  {queryOptions}     [options]
     * @return {Promise<array<DDFeature>>} Promise for a list of features
     */
    async runQuery(queryDetails, options) {
        const datasource = this.getDatasourceForType(queryDetails.featureType);
        // For features with no geometry, remove bounds and geom in search
        const ddInfo = await this.getDDInfoFor([queryDetails.featureType]);
        const featureDD = ddInfo[queryDetails.feature_type] || ddInfo[queryDetails.featureType];
        if (!featureDD.geometry_type) {
            delete options.bounds;
            delete options.geom;
        }
        return datasource.ensureLoggedIn().call('runQuery', queryDetails, options);
    },

    /**
     * Create a new detached feature
     * @param  {univFeatureType}   featureType     feature type
     * @return {Promise<Feature>} Promise for a newly created detached feature
     * @method
     */
    createDetachedFeature: getProxyMethod('createDetachedFeature'),

    /**
     * Insert a feature to a datasource. <br/>
     * Receives either a detached feature or a feature type and geojson
     * @param  {Feature|string}   detachedFeatureOrFeatureType
     * @param  {featureData}  [insertData]
     * @return {Promise<integer>}    Promise for the id of the inserted feature
     * @method
     */
    insertFeature: getProxyMethod('insertFeature'),

    /**
     * Updates a feature in a datasource. <br/>
     * Receives either an existing feature or a feature type, feature id and geojson with the changes to apply
     * @param  {Feature|string}   featureOrType
     * @param  {string}   [featureId]
     * @param  {featureData}   [updateData]
     * @return {Promise<boolean>}    Promise for the success of the operation
     * @method
     */
    updateFeature: getProxyMethod('updateFeature'),

    /**
     * Delete a feature<br/>
     * Receives either an existing feature or a feature type and id
     * @param  {Feature|string}   featureOrType
     * @param  {string}   [featureId]
     * @return {Promise}    Promise which will resolve when the operation has completed
     * @method
     */
    deleteFeature: getProxyMethod('deleteFeature'),

    /**
     * Count features of a given type with optional constraints
     * @param  {univFeatureType}    featureType
     * @param  {queryParameters}    [options]      Filters to apply on results. Check the corresponding datasource for documentation on non-supported options
     * @return {Promise<integer>}    Promise to resolve with a number of the matched features
     * @method
     */
    countFeatures: getProxyMethod('countFeatures'),

    /**
     * Switches native app mode.
     * Will ask the user for login credentials if necessary
     * @param {string} mode 'local' or 'master'
     */
    setNativeAppMode(mode) {
        if (mode === this.nativeAppMode) return; //no change

        const oldMode = this.nativeAppMode; //so we can revert if necessary

        //set the new mode. This needs to be done before asking datasources if they are logged in,
        //otherwise the question would go to the wrong instance
        this.nativeAppMode = mode;

        return Promise.map(_.values(this._datasources), datasource => {
            if (datasource.supportsReplication) {
                return datasource.ensureLoggedIn();
            }
        })
            .then(() => {
                //success, inform other components (layers)
                this.fire('nativeAppMode-changed');
            })
            .catch(reason => {
                //some datasource is not logged in
                //revert mode switch
                this.nativeAppMode = oldMode; //change to local mode should never fail, so this should always be 'local'

                throw reason;
            });
    },

    /**
     * Returns an external datasource instance
     * @param  {object}     name    Name of datasource
     * @return {IDatasource}
     */
    getDatasource(name) {
        let datasource = this._datasources[name];

        if (!datasource) {
            const datasourceDef = this._datasourceDefs[name];
            const Datasource = datasourceDef && myw.datasourceTypes[datasourceDef.type];

            if (!datasourceDef)
                throw new Error(`Unable to get definition for datasource "${name}"`);
            if (!datasourceDef.type)
                throw new Error(`Definition of  datasource '${name}' doesn't specify a type`);
            if (!Datasource)
                throw new Error(`Unable to get datasource class for type "${datasourceDef.type}"`);

            datasource = this._datasources[name] = new Datasource(this, datasourceDef);

            datasource.on('changed', e => {
                this.fire('datasourceState-changed', e);
            });
        }

        return datasource;
    },

    /**
     * Returns the name of the datasource for the given urn
     * @param  {urn}     urn
     * @return {string}
     */
    getDatasourceNameForUrn(urn) {
        return this._decomposeUrn(urn).dsName;
    },

    /**
     * Returns an external datasource instance corresponding to a given feature type
     * @param  {univFeatureType}     featureType
     * @return {IDatasource}
     */
    getDatasourceForType(featureType) {
        const dsName = this._decomposeFeatureType(featureType).dsName;
        return this.getDatasource(dsName);
    },

    saveUserDatasource(def) {
        //ENH: ignore if already datasource def with same name?
        this._datasourceDefs[def.name] = def;
        delete this._datasources[def.name]; //so a future getDatasource returns an instance from the new definition
    },

    /**
     * Registers interest in an aspect of feature details
     * When performing queries, this information can be used to optimize requests
     * @param  {string}     aspect  Aspect of feature information that a component is interested in (display_values, lobs,...)
     */
    registerInterest(aspect) {
        this._aspectsInterest[aspect] = (this._aspectsInterest[aspect] || 0) + 1;
    },
    /**
     * Deregisters interest in an aspect of feature details
     * @param  {string}     aspect  Aspect of feature's details
     */
    unregisterInterest(aspect) {
        this._aspectsInterest[aspect] = (this._aspectsInterest[aspect] || 1) - 1;
    },
    /**
     * Whether there is registered interest in a given aspect or not
     * @param  {string}     aspect  Aspect of feature's details
     * @return {boolean}
     * @private
     */
    existsInterestIn(aspect) {
        return this._aspectsInterest[aspect] > 0;
    },

    /**
     * Sets the current online status (ie. is connected to the internet) of the database
     * @param  {boolean} hasInternetAccess Whether the database should think it's online or not
     */
    setInternetStatus(hasInternetAccess) {
        if (this.hasInternetAccess !== hasInternetAccess) {
            this.hasInternetAccess = hasInternetAccess;
            this.fire('internetStatus-changed', { hasInternetAccess: hasInternetAccess });
        }
    },

    /**
     * Sets the value of a session variable
     * Session variables will be included in following types of requests:
     *        feature, select, search, render
     * @param {string} key
     * @param {literal|literal[]} value
     */
    setSessionVar(key, value) {
        if (!this._sessionVars) this._sessionVars = {};

        if (typeof value == 'undefined') {
            delete this._sessionVars[key];
        } else {
            this._sessionVars[key] = value;
        }

        if (_.isEmpty(this._sessionVars)) this._sessionVars = {};
    },

    /**
     * Returns an object with the current session variables
     * @return {object}
     */
    getSessionVars() {
        return _.clone(this._sessionVars) || {};
    },

    /**
     * @param  {univFeatureType}     univFeatureType
     * @return {object}     With properties dsName and typeInDs
     * @private
     */
    _decomposeFeatureType(univFeatureType) {
        const parts = univFeatureType.split('/');
        if (parts.length <= 1) parts.unshift('myworld');
        return {
            dsName: parts[0],
            typeInDs: parts.slice(1).join('/')
        };
    },

    /**
     * @param  {urn}     urn
     * @return {object}     With properties dsName and typeInDs and id
     * @private
     */
    _decomposeUrn(urn) {
        const parts = urn.split('/');
        if (parts.length <= 2) parts.unshift('myworld');
        return {
            dsName: parts[0],
            typeInDs: parts.slice(1, -1).join('/'),
            id: parts.slice(-1)[0]
        };
    },

    /**
     * Ensures that datasource definitions are available.
     * This ensures that 'getDatasource()' can subsequently be called synchronously
     * @return {promise}
     * @private
     */
    _ensureDatasourceDefs() {
        //system caches the request
        const request = this.system.getStartupInfo(this.applicationName);

        return request.then(result => {
            //store keyed on datasource name
            _.each(result.datasources, def => {
                def.layerDefs = _.filter(
                    result.layers,
                    layerDef => layerDef.datasource == def.name
                );

                this._datasourceDefs[def.name] = def;
            });
        });
    }
});

/**
 * A string specifiyng a feature type across all datasources. <br/>
 * Format is '&lt;datasourceName&gt;/&lt;featureTypeInDatasource&gt;' <br/>
 * For the myworld datasource, its name is ommited and the format is only '&lt;featureTypeInDatasource&gt;'
 * @typedef {string} univFeatureType
 */

/**
 * A string specifiyng a universal feature identifier <br/>
 * Format is '&lt;datasourceName&gt;/&lt;featureTypeInDatasource&gt;/&lt;featureId&gt;' <br/>
 * For the myworld datasource, its name is ommited and the format is only '&lt;featureTypeInDatasource&gt;/&lt;featureId&gt;'
 * @typedef {string} urn
 */

/**
 * Data dictionary of a feature type
 * @typedef featureDD
 * @property  {Datasource}  datasource          Datasource instance for features of this type
 * @property  {string}          name                Name of feature type ()
 * @property  {univFeatureType} ufn                 Feature type identifier across all datasources
 * @property  {string}          external_name
 * @property  {Object}          fields
 * @property  {string}          geometry_type       'point', 'linestring', 'polygon'
 * @property  {Array<fieldGroup>} field_groups
 * @property  {Array<string>}   fields_order
 * @property  {Object}          defaults
 * @property  {boolean}         editable
 * @property  {boolean}         insert_from_gui
 * @property  {boolean}         update_from_gui
 * @property  {boolean}         delete_from_gui
 * @property  {boolean}         track_changes
 */

/**
 * Data dictionary of a feature type
 * @typedef fieldGroup
 * @property  {string}          display_name
 * @property  {integer}         position
 * @property  {Array<{field_name: string, position:integer}>}          fields
 * @property  {boolean}         is_expanded
 */

/**
 * Details about the results of a query
 * @typedef queryDetails
 * @property  {queryDefinition} def         Query definition as generated by its datasource
 * @property  {queryOptions}    options
 * @property  {array<Feature>} result
 * @property  {integer}         count       Number or records included in the query result
 * @property  {integer}         totalCount  Total number of records if a limit was not specified. A value of Infinity means that this value was not determined
 * @property  {integer}         limit       Max number of records requested
 * @property  {integer}         offset      Requested offset (value of 'offset' in queryParams)
 */

/**
 * Definition of a query.
 * Aditional properties may be present - these will be specific to the corresponding datasource
 * @typedef queryDefinition
 * @property {string}               feature_type             Type of features to query
 * @property {string}               [spatial_restriction]    'window' or 'selection'
 * @property {array<queryClause>}   [clauses]                Clauses that restrict the features to include in results
 */

/**
 * An attribute clause to use when querying a feature table
 * @typedef queryClause
 * @property {string}           fieldName       Field name to filter on
 * @property {string}           operator        operator to use. One of =, <>, >=, <=, >, <, like, ilike
 * @property {string}           value           Value to filter on
 */

/**
 * Context options for a query
 * @typedef queryOptions
 * @property  {L.LatLngBounds}   [bounds]   Bounds to restrict results to
 * @property  {geojsonGeom}      [polygon]  Polygon to restrict results to
 * @property  {boolean}          [displayValues=false]
 */

/**
 * Parameters for a query
 * @typedef queryParameters
 * @property  {L.LatLngBounds} [bounds]    Bounding box to filter the results on
 * @property  {geojsonGeom}    [geom]      Geometry to filter results on (results have to interact with geometry)
 * @property  {Array<queryClause>}  [clauses]   List of attribute clauses to use in filter
 * @property  {string}         [filter]    Expression in myworld query language to filter results (myWorld datasource only)
 * @property  {integer}        [limit]     Max number of records to return.
 *                                         If the provided value is null then no limit will be applied.
 *                                         If no value is provided, defaults to the 'queryResultLimit' database setting.
 * @property  {integer}        [offset]    Return the results starting from the given offset
 * @property  {boolean}        [includeTotal=false]     Whether to calculate the total number of results (without the limit) even if it is costly. Defaults to false. (myWorld datasource only)
 * @property  {boolean}        [displayValues=false]     Whether to include additional information about some of the fields (for display purposes). Defaults to true. (myWorld datasource only)
 * @property  {boolean}        [includeLobs=false]      Whether to include 'large object' (eg. image) fields or not. Defaults to false. (myWorld datasource only)
 * @property  {boolean}        [includeGeoGeometry=false]  Whether to include geo location geometry for internals objects. Defaults to false. (myWorld datasource only)
 * @property  {Array<orderClause>}   [orderBy] Columns to sort on and options
 */

/**
 * An attribute clause to use when querying a feature table
 * @typedef orderClause
 * @property {string}           fieldName       Field name to filter on
 * @property {boolean}          [descending=false]  Controls sort order
 */

export default myw.Database;
